import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContractCreateEditState, useContractCreateEditDispatch } from './contract-create-edit-context';
import { ACTION_TYPES } from './contract-create-edit-reducer';
import { IContractAddEditRequest } from '../../../../model/scp/contract/request/contract-add-edit-request';
import { IFileAddRequest } from '../../../../model/scp/base/request/file-add-request';
import BaseAppService from '../../../../application/platform/scp/base/base-app-service';
import EntityAppService from '../../../../application/platform/scp/entity/entitiy-app-service';
import PurchaseAppService from '../../../../application/platform/scp/purchase/purchase-app-service';
import ContractAppService from '../../../../application/platform/scp/contract/contract-app-service';
import { DefaultDialogProps } from '../../../../components/default-dialog';
import { useBaseLayout } from '../../../../components/context/use-base-layout';
import { useBaseData } from '../../../../application/context/base-data-context';

export const useContractCreateEditController = () => {
    const navigate = useNavigate();
    const dispatch = useContractCreateEditDispatch();
    const state = useContractCreateEditState();
    const { showLoading, showMessage, showDialog } = useBaseLayout();
    const [isSaving, setIsSaving] = useState(false);
    const baseData = useBaseData();

    useEffect(() => {
        loadInitialData();
    }, [state.contractId]);

    useEffect(() => {
        if (baseData.loaded) {
            dispatch({ type: ACTION_TYPES.setTypeContract, payload: baseData.typeContract });
            dispatch({ type: ACTION_TYPES.setCategoryProcess, payload: baseData.categoryProcess });
        }
    }, [baseData.loaded]);

    async function loadInitialData() {
        try {
            showLoading({ open: true });

            const [purchaseData, unidEntityData, typeDocumentData] = await Promise.all([
                PurchaseAppService.listAllPurchase(),
                EntityAppService.listUnidEntity(),
                BaseAppService.listTypeDocument(3)
            ]);

            dispatch({ type: ACTION_TYPES.setPurchases, payload: purchaseData });
            dispatch({ type: ACTION_TYPES.setUnidEntity, payload: unidEntityData });
            dispatch({ type: ACTION_TYPES.setTypeDocument, payload: typeDocumentData });

            if (state.contractId !== undefined && state.contractId !== null) {
                const data = await ContractAppService.getContract(Number(state.contractId));

                const mappedData: IContractAddEditRequest = {
                    ...data,
                    id: data.id,
                    clientId: data.clientId,
                    purchaseId: data.purchaseId,
                    contractEffortNumber: data.contractEffortNumber,
                    yearContract: data.yearContract,
                    processNumber: data.processNumber,
                    indRevenue: data.indRevenue,
                    supplierNumberIdentification: data.supplierNumberIdentification,
                    supplierType: data.supplierType,
                    supplierName: data.supplierName,
                    supplierNumberIdentificationSubContract: data.supplierNumberIdentificationSubContract,
                    supplierTypeSubContract: data.supplierTypeSubContract,
                    supplierNameSubContract: data.supplierNameSubContract,
                    object: data.object,
                    notes: data.notes,
                    initialValue: data.initialValue,
                    numberPaymentInstallment: data.numberOfInstallments,
                    paymentInstallmentlValue: data.paymentInstallmentValue,
                    globalValue: data.globalValue,
                    accumulatedValue: data.accumulatedValue,
                    signDate: data.signDate,
                    effectiveDateStart: data.effectiveDateStart,
                    effectiveDateEnd: data.effectiveDateEnd,
                    identifyCIPI: data.identifyCip,
                    urlCIPI: data.urlCipi,
                };

                dispatch({ type: ACTION_TYPES.setContractRequest, payload: mappedData });
            }

            showLoading({ open: false });
        } catch (error) {
            showLoading({ open: false });
            showMessage({
                open: true,
                message: "Não foi possível carregar os dados do contrato",
                detail: (error as any).details || (error as Error).message,
                type: "error"
            });
        }
    }

    function handleOnChange(e: any) {
        const name = e.target.name;
        const value = e.target.value;

        const fieldMap: Record<string, string> = {
            'input-process-number': 'processNumber',
            'input-year-contract': 'yearContract',
            'input-number-contract-effort': 'contractEffortNumber',
            'select-cateogry-process': 'categoryProcessId',
            'select-purchase': 'purchaseId',
            'select-unid-entity': 'codBuyerUnit',
            'select-contract-type': 'contractTypeId',
            'select-supplier-type': 'supplierType',
            'input-identfiy-cipi': 'identifyCIPI',
            'input-url-cipi': 'urlCIPI',
            'input-supplier-number-identification': 'supplierNumberIdentification',
            'input-supplier-name': 'supplierName',
            'select-supplier-type-subcontract': 'supplierTypeSubContract',
            'input-supplier-number-identification-subcontract': 'supplierNumberIdentificationSubContract',
            'input-supplier-name-subcontract': 'supplierNameSubContract',
            'input-object': 'object',
            'input-notes': 'notes',
            'input-initial-value': 'initialValue',
            'input-number-payment-installment': 'numberPaymentInstallment',
            'input-payment-installment-value': 'paymentInstallmentlValue',
            'input-global-value': 'globalValue',
            'input-acumulated-value': 'accumulatedValue',
            'select-type-document-id': 'typeDocumentId',
            'input-document-title': 'documentTitle',
        };

        const field = fieldMap[name];
        if (field) {
            dispatch({
                type: ACTION_TYPES.updateContractRequest,
                payload: { [field]: value }
            });
        }
    }

    function handleDateChange(field: string, date: any) {
        if (date && date.isValid && date.isValid()) {
            const formattedDate = date.format("YYYY-MM-DD");
            dispatch({
                type: ACTION_TYPES.updateContractRequest,
                payload: { [field]: formattedDate }
            });
        } else {
            dispatch({
                type: ACTION_TYPES.updateContractRequest,
                payload: { [field]: undefined }
            });
        }
    }

    function setTabIndex(index: number) {
        dispatch({ type: ACTION_TYPES.setTabIndex, payload: index });
    }

    function setFiles(files: IFileAddRequest[]) {
        dispatch({ type: ACTION_TYPES.setFiles, payload: files });
    }

    function setFileUpload(file: File | undefined) {
        dispatch({ type: ACTION_TYPES.setFile, payload: file });
    }

    function validFields(): boolean {
        const { contractRequest, contractId, file } = state;

        const requiredFields = [
            { value: contractRequest.purchaseId, condition: (v: any) => v !== null && v !== undefined },
            { value: contractRequest.processNumber, condition: (v: any) => v && v.length > 0 },
            { value: contractRequest.contractEffortNumber, condition: (v: any) => v && v.length > 0 },
            { value: contractRequest.yearContract, condition: (v: any) => v !== null && v !== undefined },
            { value: contractRequest.codBuyerUnit, condition: (v: any) => v && v.length > 0 },
            { value: contractRequest.contractTypeId, condition: (v: any) => v !== null && v !== undefined },
            { value: contractRequest.categoryProcessId, condition: (v: any) => v !== null && v !== undefined },
            { value: contractRequest.signDate, condition: (v: any) => v && v.length > 0 },
            { value: contractRequest.effectiveDateStart, condition: (v: any) => v && v.length > 0 },
            { value: contractRequest.effectiveDateEnd, condition: (v: any) => v && v.length > 0 },
            { value: contractRequest.supplierType, condition: (v: any) => v && v.length > 0 },
            { value: contractRequest.supplierNumberIdentification, condition: (v: any) => v && v.length > 0 },
            { value: contractRequest.supplierName, condition: (v: any) => v && v.length > 0 },
            { value: contractRequest.object, condition: (v: any) => v && v.length > 0 },
            { value: contractRequest.initialValue, condition: (v: any) => v !== null && v !== undefined && v !== 0 },
            { value: contractRequest.numberPaymentInstallment, condition: (v: any) => v !== null && v !== undefined && v !== 0 },
            { value: contractRequest.globalValue, condition: (v: any) => v !== null && v !== undefined && v !== 0 },
            { value: contractRequest.accumulatedValue, condition: (v: any) => v !== null && v !== undefined && v !== 0 },
        ];

        const baseValid = requiredFields.every(field => field.condition(field.value));

        if (!contractId) {
            return baseValid
                && (contractRequest.typeDocumentId !== null && contractRequest.typeDocumentId !== undefined && contractRequest.typeDocumentId !== 0)
                && (contractRequest.documentTitle !== null && contractRequest.documentTitle !== undefined && contractRequest.documentTitle.length > 0)
                && (file !== null && file !== undefined);
        }

        return baseValid;
    }

    async function addEditContract() {
        setIsSaving(true);
        try {
            showLoading({ open: true });

            if (state.contractId === undefined || state.contractId === null) {
                await ContractAppService.addContract(state.contractRequest, state.file);
                showMessage({ open: true, message: "Contrato adicionado com sucesso!", type: "success" });
            } else {
                await ContractAppService.updateContract(Number(state.contractId), state.contractRequest);
                showMessage({ open: true, message: "Contrato atualizado com sucesso!", type: "success" });
            }

            showLoading({ open: false });

            showDialog({
                open: true,
                message: "Deseja adicionar um novo contrato?",
                onYesClick: () => {
                    dispatch({ type: ACTION_TYPES.resetState, payload: new Date().getFullYear() });
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    navigate("/contratos/publicar");
                },
                onNoClick: () => {
                    navigate("/contratos");
                }
            } as DefaultDialogProps);
        } catch (error) {
            showLoading({ open: false });
            showMessage({
                open: true,
                message: "Não foi possível salvar o contrato",
                detail: (error as any).details || (error as Error).message,
                type: "error"
            });
        } finally {
            setIsSaving(false);
        }
    }

    function handleSave() {
        if (validFields()) {
            showDialog({
                open: true,
                message: "Deseja salvar os dados do contrato?",
                onYesClick: () => {
                    addEditContract();
                }
            } as DefaultDialogProps);
        } else {
            dispatch({ type: ACTION_TYPES.setShowValidationErrors, payload: true });
            showMessage({ open: true, message: "Preencha todos os campos obrigatórios", type: "error" });
        }
    }

    return {
        handleOnChange,
        handleDateChange,
        handleSave,
        setTabIndex,
        setFiles,
        setFileUpload,
        validFields,
        isSaving
    };
};
